import { useState } from 'react'
import { useETSFIncidents, type IncidentFormData, type ETSFIncident } from '../../../hooks/useETSFIncidents'
import { useAuth } from '../../../contexts/AuthContext'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

const INCIDENT_TYPES = {
  unauthorised_removal: 'Unauthorised Removal',
  cctv_failure: 'CCTV Failure',
  stock_discrepancy: 'Stock Discrepancy',
  hmrc_visit: 'HMRC Visit',
  access_breach: 'Access Breach',
  unmanifested_goods: 'Unmanifested Goods',
  other: 'Other',
}

const SEVERITY_COLORS = {
  low: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' },
  medium: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' },
  high: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' },
  critical: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' },
}

interface IncidentsTabProps {
  location: 'BHX' | 'LHR'
}

export default function IncidentsTab({ location }: IncidentsTabProps) {
  const { userEmail } = useAuth()
  const { incidents, loading, error, createIncident, markResolved, getStats } = useETSFIncidents(location)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [selectedIncident, setSelectedIncident] = useState<ETSFIncident | null>(null)
  const [showResolutionForm, setShowResolutionForm] = useState(false)
  const [resolutionAction, setResolutionAction] = useState('')
  const [resolvingIncidentId, setResolvingIncidentId] = useState<string | null>(null)

  const [formData, setFormData] = useState<IncidentFormData>({
    location,
    incident_date: new Date().toISOString().split('T')[0],
    incident_type: 'stock_discrepancy',
    description: '',
    severity: 'medium',
    reported_to_hmrc: false,
    reported_to_hmrc_date: null,
    hmrc_reference: null,
    action_taken: null,
  })

  const stats = getStats()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setFormError(null)

    try {
      if (!formData.description.trim()) {
        throw new Error('Description is required')
      }

      await createIncident({
        ...formData,
        created_by: userEmail || 'System',
      })

      setShowForm(false)
      setFormData({
        location,
        incident_date: new Date().toISOString().split('T')[0],
        incident_type: 'stock_discrepancy',
        description: '',
        severity: 'medium',
        reported_to_hmrc: false,
        reported_to_hmrc_date: null,
        hmrc_reference: null,
        action_taken: null,
      })
    } catch (err: any) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleResolve = async () => {
    if (!selectedIncident || !resolutionAction.trim()) return

    setResolvingIncidentId(selectedIncident.id)
    try {
      await markResolved(selectedIncident.id, resolutionAction)
      setSelectedIncident(null)
      setShowResolutionForm(false)
      setResolutionAction('')
    } catch (err: any) {
      setFormError(err.message)
    } finally {
      setResolvingIncidentId(null)
    }
  }

  const openIncidents = incidents.filter(i => !i.resolved)
  const resolvedIncidents = incidents.filter(i => i.resolved)

  if (loading) return <div className="p-6">Loading incidents...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Open Incidents</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{openIncidents.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Critical Unresolved</div>
          <div className="text-3xl font-bold text-red-600 dark:text-red-400 mt-1">{stats.criticalCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">High Priority</div>
          <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-1">{stats.highCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Critical (This Month)</div>
          <div className="text-3xl font-bold text-red-600 dark:text-red-400 mt-1">{stats.criticalThisMonth}</div>
        </div>
      </div>

      {/* Report Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+</span> Report Incident
        </button>
      </div>

      {/* Incident Form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Report New Incident</h3>
          {formError && <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded mb-4">{formError}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLS}>Incident Date</label>
                <input
                  type="date"
                  value={formData.incident_date}
                  onChange={e => setFormData({ ...formData, incident_date: e.target.value })}
                  className={INPUT_CLS}
                  required
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Incident Type</label>
                <select
                  value={formData.incident_type}
                  onChange={e => setFormData({ ...formData, incident_type: e.target.value as any })}
                  className={INPUT_CLS}
                  required
                >
                  {Object.entries(INCIDENT_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLS}>Severity</label>
                <select
                  value={formData.severity}
                  onChange={e => setFormData({ ...formData, severity: e.target.value as any })}
                  className={INPUT_CLS}
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>HMRC Reference (if applicable)</label>
                <input
                  type="text"
                  value={formData.hmrc_reference || ''}
                  onChange={e => setFormData({ ...formData, hmrc_reference: e.target.value || null })}
                  placeholder="e.g. HMRC/2024/001"
                  className={INPUT_CLS}
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLS}>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of the incident"
                className={INPUT_CLS + ' h-32'}
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="reported"
                checked={formData.reported_to_hmrc}
                onChange={e => setFormData({ ...formData, reported_to_hmrc: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="reported" className="text-sm text-gray-700 dark:text-gray-300">
                Reported to HMRC
              </label>
            </div>

            {formData.reported_to_hmrc && (
              <div>
                <label className={LABEL_CLS}>Date Reported to HMRC</label>
                <input
                  type="date"
                  value={formData.reported_to_hmrc_date || ''}
                  onChange={e => setFormData({ ...formData, reported_to_hmrc_date: e.target.value || null })}
                  className={INPUT_CLS}
                />
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Report Incident'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Open Incidents */}
      {openIncidents.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4">Open Incidents ({openIncidents.length})</h3>
          <div className="space-y-3">
            {openIncidents.map(incident => (
              <div key={incident.id} className={`rounded-lg border p-4 ${SEVERITY_COLORS[incident.severity].bg}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm font-bold ${SEVERITY_COLORS[incident.severity].text}`}>
                        {INCIDENT_TYPES[incident.incident_type]}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${SEVERITY_COLORS[incident.severity].badge}`}>
                        {incident.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{incident.description}</p>
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <p>Reported: {new Date(incident.incident_date).toLocaleDateString('en-GB')}</p>
                      {incident.reported_to_hmrc && (
                        <p>HMRC Notified: {incident.hmrc_reference || 'Yes'}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedIncident(incident)
                      setShowResolutionForm(true)
                    }}
                    className="ml-4 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 whitespace-nowrap"
                  >
                    Mark Resolved
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolved Incidents */}
      {resolvedIncidents.length > 0 && (
        <div>
          <h3 className="text-lg font-bold mb-4">Resolved Incidents ({resolvedIncidents.length})</h3>
          <div className="space-y-3">
            {resolvedIncidents.map(incident => (
              <div key={incident.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4 opacity-60">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-400">
                        {INCIDENT_TYPES[incident.incident_type]}
                      </span>
                      <span className="text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900 px-2 py-1 rounded">
                        RESOLVED
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{incident.description}</p>
                    {incident.action_taken && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span className="font-medium">Action Taken:</span> {incident.action_taken}
                      </p>
                    )}
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <p>Resolved: {new Date(incident.resolved_date || incident.updated_at).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolution Form Modal */}
      {showResolutionForm && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Mark Incident as Resolved</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{selectedIncident.description}</p>

            <textarea
              value={resolutionAction}
              onChange={e => setResolutionAction(e.target.value)}
              placeholder="Describe the action taken to resolve this incident"
              className={INPUT_CLS + ' h-24 mb-4'}
            />

            <div className="flex gap-2">
              <button
                onClick={handleResolve}
                disabled={!resolutionAction.trim() || resolvingIncidentId === selectedIncident.id}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {resolvingIncidentId === selectedIncident.id ? 'Marking...' : 'Mark Resolved'}
              </button>
              <button
                onClick={() => {
                  setShowResolutionForm(false)
                  setSelectedIncident(null)
                  setResolutionAction('')
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
